import { useState, useEffect, useMemo, useCallback } from "react";
import { Search, ChevronDown, Filter, Loader2, ChevronLeft, ChevronRight, X, Waves, Mountain, Sunset, Shield, MapPin, Clock, Users, Bus } from "lucide-react";
import Header from "@/components/Header";
import CourseCard from "@/components/CourseCard";
import CourseCardSkeleton from "@/components/CourseCardSkeleton";
import Footer from "@/components/Footer";
import { Link, useSearchParams } from "react-router-dom";
import { useCourses, type Course } from "@/hooks/useCourses";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { trackCourseSearch } from "@/lib/activity-tracker";
import { TRAIL_NAMES, getAllTrails } from "@/constants/trails";
const CourseSearch = () => {
  const [searchParams] = useSearchParams();
  
  // URL 파라미터에서 트레일 이름을 확인하여 지역 설정
  const getInitialRegionFromURL = () => {
    const trailParam = searchParams.get('trail');
    if (trailParam) {
      // 트레일 이름에 따라 지역 매핑
      if (trailParam.includes(TRAIL_NAMES["T_THEME_MNG0000011235"])) {
        return "동해안";
      } else if (trailParam.includes(TRAIL_NAMES["T_ROUTE_MNG0000000001"])) {
        return "남해안";
      } else if (trailParam.includes(TRAIL_NAMES["T_ROUTE_MNG0000000043"])) {
        return "서해안";
      } else if (trailParam.includes(TRAIL_NAMES["T_ROUTE_MNG0000000047"])) {
        return "DMZ";
      }
    }
    
    // localStorage에서 마지막 선택값을 불러오거나 디폴트로 해파랑길(동해안) 설정
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const savedRegion = localStorage.getItem('wholsee-selected-region');
        return savedRegion || "동해안";
      } catch (error) {
        // localStorage access failed, use default
        return "동해안";
      }
    }
    return "동해안";
  };

  const [selectedRegion, setSelectedRegion] = useState(getInitialRegionFromURL());
  const [selectedDistance, setSelectedDistance] = useState("전체");
  const [selectedDuration, setSelectedDuration] = useState("전체");
  const [selectedDifficulty, setSelectedDifficulty] = useState("전체");
  const [sortBy, setSortBy] = useState("추천순");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [tabletFiltersCollapsed, setTabletFiltersCollapsed] = useState(false);
  const itemsPerPage = 21;
  const isMobile = useIsMobile();
  const {
    data: courses,
    isLoading,
    error
  } = useCourses();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchKeyword);
      setCurrentPage(1); // Reset to first page when search changes
    }, 300);
    return () => clearTimeout(timer);
  }, [searchKeyword]);

  // selectedRegion이 변경될 때마다 localStorage에 저장
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.setItem('wholsee-selected-region', selectedRegion);
      } catch (error) {
        // localStorage access failed, ignore
        console.warn('Failed to save region to localStorage:', error);
      }
    }
  }, [selectedRegion]);
  const regions = ["전체", "동해안", "남해안", "서해안", "DMZ", "부산", "경남", "전남", "충남", "경기", "인천", "전북", "경북"];
  const distances = ["전체", "10km 이하", "10-15km", "15-20km", "20km 이상"];
  const durations = ["전체", "3시간 이하", "3-5시간", "5-7시간", "7시간 이상"];
  const difficulties = ["전체", "쉬움", "보통", "어려움"];
  const sortOptions = ["추천순", "거리순", "시간순"];

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedRegion !== "전체") count++;
    if (selectedDistance !== "전체") count++;
    if (selectedDuration !== "전체") count++;
    if (selectedDifficulty !== "전체") count++;
    return count;
  }, [selectedRegion, selectedDistance, selectedDuration, selectedDifficulty]);

  // Memoized filtering and searching logic
  const filteredCourses = useMemo(() => {
    if (!courses) return [];
    return courses.filter(course => {
      const courseLocation = course.start_point || "";
      const courseDistance = course.length || 0;
      const courseDuration = course.estimated_time || 0;
      const courseDifficulty = course.difficulty;
      const courseName = course.name;
      const courseDescription = course.description || course.short_desc || "";

      // 지역 필터 - trail_name을 우선하고 start_point로 보완
      let matchesRegion = selectedRegion === "전체";
      if (!matchesRegion) {
        // 둘레길 기반 매칭 (우선순위)
        if (selectedRegion === "동해안" && course.trail_name?.includes(TRAIL_NAMES["T_THEME_MNG0000011235"])) {
          matchesRegion = true;
        } else if (selectedRegion === "남해안" && course.trail_name?.includes(TRAIL_NAMES["T_ROUTE_MNG0000000001"])) {
          matchesRegion = true;
        } else if (selectedRegion === "서해안" && course.trail_name?.includes(TRAIL_NAMES["T_ROUTE_MNG0000000043"])) {
          matchesRegion = true;
        } else if (selectedRegion === "DMZ" && course.trail_name?.includes(TRAIL_NAMES["T_ROUTE_MNG0000000047"])) {
          matchesRegion = true;
        } else {
          // start_point 기반 지역 매칭
          matchesRegion = selectedRegion === "부산" && courseLocation.includes("부산") || selectedRegion === "경남" && (courseLocation.includes("경남") || courseLocation.includes("창원") || courseLocation.includes("거제") || courseLocation.includes("통영")) || selectedRegion === "전남" && (courseLocation.includes("전남") || courseLocation.includes("여수") || courseLocation.includes("순천") || courseLocation.includes("해남")) || selectedRegion === "충남" && (courseLocation.includes("충남") || courseLocation.includes("태안")) || selectedRegion === "경기" && (courseLocation.includes("경기") || courseLocation.includes("김포") || courseLocation.includes("화성")) || selectedRegion === "인천" && courseLocation.includes("인천") || selectedRegion === "전북" && (courseLocation.includes("전북") || courseLocation.includes("군산")) || selectedRegion === "경북" && (courseLocation.includes("경북") || courseLocation.includes("포항") || courseLocation.includes("울산")) || selectedRegion === "강원" && (courseLocation.includes("강원") || courseLocation.includes("강릉") || courseLocation.includes("속초") || courseLocation.includes("고성"));
        }
      }

      // 거리 필터
      const matchesDistance = selectedDistance === "전체" || selectedDistance === "10km 이하" && courseDistance <= 10 || selectedDistance === "10-15km" && courseDistance > 10 && courseDistance <= 15 || selectedDistance === "15-20km" && courseDistance > 15 && courseDistance <= 20 || selectedDistance === "20km 이상" && courseDistance > 20;

      // 소요시간 필터
      let matchesDuration = selectedDuration === "전체";
      if (!matchesDuration) {
        const hours = courseDuration / 60;
        matchesDuration = selectedDuration === "3시간 이하" && hours <= 3 || selectedDuration === "3-5시간" && hours > 3 && hours <= 5 || selectedDuration === "5-7시간" && hours > 5 && hours <= 7 || selectedDuration === "7시간 이상" && hours > 7;
      }

      // 난이도 필터
      const matchesDifficulty = selectedDifficulty === "전체" || courseDifficulty === selectedDifficulty;

      // 검색 키워드 필터 (debounced)
      const matchesSearch = debouncedSearch === "" || courseName.toLowerCase().includes(debouncedSearch.toLowerCase()) || courseLocation.toLowerCase().includes(debouncedSearch.toLowerCase()) || courseDescription.toLowerCase().includes(debouncedSearch.toLowerCase());
      return matchesRegion && matchesDistance && matchesDuration && matchesDifficulty && matchesSearch;
    });
  }, [courses, selectedRegion, selectedDistance, selectedDuration, selectedDifficulty, debouncedSearch]);

  // Memoized sorting logic
  const sortedCourses = useMemo(() => {
    return [...filteredCourses].sort((a, b) => {
      switch (sortBy) {
        case "거리순":
          return (a.length || 0) - (b.length || 0);
        case "시간순":
          return (a.estimated_time || 0) - (b.estimated_time || 0);
        default:
          {
            // 추천순 (recommend_count 높은 순서, 동일하면 course_number 순서)
            const recommendDiff = (b.recommend_count || 0) - (a.recommend_count || 0);
            return recommendDiff !== 0 ? recommendDiff : a.course_number - b.course_number;
          }
      }
    });
  }, [filteredCourses, sortBy]);

  // 검색 및 필터 변경 시 활동 추적
  useEffect(() => {
    if (sortedCourses && (debouncedSearch || selectedRegion !== "전체" || selectedDistance !== "전체" || selectedDuration !== "전체" || selectedDifficulty !== "전체")) {
      const searchContext = {
        keyword: debouncedSearch,
        region: selectedRegion,
        distance: selectedDistance,
        duration: selectedDuration,
        difficulty: selectedDifficulty
      };
      const searchQueryString = Object.entries(searchContext).filter(([_, value]) => value && value !== "전체").map(([key, value]) => `${key}:${value}`).join(', ') || '전체';
      trackCourseSearch(searchQueryString, sortedCourses.length);
    }
  }, [debouncedSearch, selectedRegion, selectedDistance, selectedDuration, selectedDifficulty, sortedCourses]);

  // Pagination logic
  const totalPages = Math.ceil(sortedCourses.length / itemsPerPage);
  const paginatedCourses = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedCourses.slice(startIndex, endIndex);
  }, [sortedCourses, currentPage, itemsPerPage]);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // Smooth scroll to top of results
    document.querySelector('[data-testid="course-results"]')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, []);
  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, handlePageChange]);
  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, handlePageChange]);

  // Filter Select Component
  const FilterSelect = ({
    label,
    value,
    onChange,
    options,
    className = ""
  }: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: string[];
    className?: string;
  }) => <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} className={`w-full p-3 pr-10 border rounded-lg appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-[#F39800] focus:border-transparent text-base transition-all ${value !== "전체" ? "border-[#F39800] bg-[#F39800]/5" : "border-gray-300"} ${className}`}>
          {options.map(option => <option key={option} value={option}>
              {option}
            </option>)}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
      </div>
    </div>;

  // Filter Content Component (reusable for all layouts)
  const FilterContent = ({
    showApplyButton = false,
    onApply = () => {}
  }: {
    showApplyButton?: boolean;
    onApply?: () => void;
  }) => <div className="space-y-6">
      <FilterSelect label="지역" value={selectedRegion} onChange={setSelectedRegion} options={regions} />
      <FilterSelect label="거리" value={selectedDistance} onChange={setSelectedDistance} options={distances} />
      <FilterSelect label="소요시간" value={selectedDuration} onChange={setSelectedDuration} options={durations} />
      <FilterSelect label="난이도" value={selectedDifficulty} onChange={setSelectedDifficulty} options={difficulties} />
      
      {showApplyButton && <Button className="w-full" onClick={onApply}>
          필터 적용
        </Button>}
    </div>;

  // Sort Dropdown Component
  const SortDropdown = () => (
    <div className="relative">
      <select 
        value={sortBy} 
        onChange={e => setSortBy(e.target.value)} 
        className="p-2 pr-8 border rounded-lg appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-[#F39800] focus:border-transparent text-sm transition-all border-gray-300"
      >
        {sortOptions.map(option => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
    </div>
  );
  return <div className="min-h-screen bg-gray-50 pb-mobile-nav">
      <Header />
      
      {/* Page Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            홀씨와 함께 떠나는 전국 둘레길
          </h1>
          <p className="text-lg text-gray-600">
            편안하고 안전한 프리미엄 트레킹 여행을 경험해보세요
          </p>
        </div>
      </div>

      {/* Korea Trail Hero Section - Modern & Compact */}
      <div className="relative bg-gradient-to-r from-slate-50 via-white to-slate-50 overflow-hidden border-b">
        <div className="absolute inset-0 bg-[url('/images/image6.webp')] bg-cover bg-center opacity-10"></div>
        <div className="relative container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-[#F39800]/10 text-[#F39800] px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Mountain className="w-4 h-4" />
                <span>프리미엄 트레킹 투어</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
                코리아둘레길과 함께하는 <span className="text-[#F39800]">특별한 여행</span>
              </h2>
              
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
                총 <span className="font-bold text-[#F39800]">4,500km</span>의 초장거리 걷기여행길을 편안하고 안전하게 경험해보세요
              </p>
            </div>

            {/* Main Content Grid */}
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left: Core Values */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-6 shadow-sm border hover:shadow-md transition-all group">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <Shield className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">편안</div>
                      <div className="text-sm text-gray-500">Peace</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-6 shadow-sm border hover:shadow-md transition-all group">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                      <Users className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">동행</div>
                      <div className="text-sm text-gray-500">Together</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-6 shadow-sm border hover:shadow-md transition-all group">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                      <Mountain className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">힐링</div>
                      <div className="text-sm text-gray-500">Healing</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-6 shadow-sm border hover:shadow-md transition-all group">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                      <Waves className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">공존</div>
                      <div className="text-sm text-gray-500">Coexistence</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Service Features */}
              <div className="bg-white/90 backdrop-blur-sm rounded-xl p-6 shadow-sm border">
                <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#F39800]" />
                  홀씨와 함께하는 특별함
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-700">
                    <Bus className="w-5 h-5 text-[#F39800]" />
                    <span>편안한 리무진 버스로 이동</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Users className="w-5 h-5 text-[#F39800]" />
                    <span>전문 가이드와 안전한 동행</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Clock className="w-5 h-5 text-[#F39800]" />
                    <span>당일 완주 트레킹 투어</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-600">
                    자연과 문화, 역사가 어우러진 대한민국의 아름다운 길을 걸으며 새로운 만남과 소중한 추억을 만들어보세요
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Four Major Trails Simplified Section */}
      <div className="bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              4대 코리아둘레길
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              각각의 둘레길은 고유한 매력과 특색을 가지고 있습니다. 원하시는 둘레길을 선택하여 특별한 여행을 시작해보세요.
            </p>
          </div>

          {/* Simplified Trail Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 해파랑길 */}
            <div onClick={() => setSelectedRegion("동해안")} className={`p-5 rounded-xl cursor-pointer transition-all duration-300 group ${selectedRegion === "동해안" ? "bg-blue-500 text-white shadow-lg scale-105" : "bg-white hover:bg-blue-50 border border-gray-200 hover:shadow-md hover:scale-102"}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${selectedRegion === "동해안" ? "bg-white/20" : "bg-blue-100 group-hover:bg-blue-200"}`}>
                  <Waves className={`w-6 h-6 ${selectedRegion === "동해안" ? "text-white" : "text-blue-600"}`} />
                </div>
                <div>
                  <h3 className="text-lg font-bold">{TRAIL_NAMES["T_THEME_MNG0000011235"]}</h3>
                  <div className="text-xs opacity-75">50개 코스 · 750km</div>
                </div>
              </div>
              <p className="text-sm opacity-90 leading-relaxed">동해안의 푸른 바다와 함께 걷는 길</p>
            </div>

            {/* 남파랑길 */}
            <div onClick={() => setSelectedRegion("남해안")} className={`p-5 rounded-xl cursor-pointer transition-all duration-300 group ${selectedRegion === "남해안" ? "bg-emerald-500 text-white shadow-lg scale-105" : "bg-white hover:bg-emerald-50 border border-gray-200 hover:shadow-md hover:scale-102"}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${selectedRegion === "남해안" ? "bg-white/20" : "bg-emerald-100 group-hover:bg-emerald-200"}`}>
                  <Mountain className={`w-6 h-6 ${selectedRegion === "남해안" ? "text-white" : "text-emerald-600"}`} />
                </div>
                <div>
                  <h3 className="text-lg font-bold">{TRAIL_NAMES["T_ROUTE_MNG0000000001"]}</h3>
                  <div className="text-xs opacity-75">91개 코스 · 1,463km</div>
                </div>
              </div>
              <p className="text-sm opacity-90 leading-relaxed">남쪽의 쪽빛 바다와 함께 걷는 길</p>
            </div>

            {/* 서해랑길 */}
            <div onClick={() => setSelectedRegion("서해안")} className={`p-5 rounded-xl cursor-pointer transition-all duration-300 group ${selectedRegion === "서해안" ? "bg-amber-500 text-white shadow-lg scale-105" : "bg-white hover:bg-amber-50 border border-gray-200 hover:shadow-md hover:scale-102"}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${selectedRegion === "서해안" ? "bg-white/20" : "bg-amber-100 group-hover:bg-amber-200"}`}>
                  <Sunset className={`w-6 h-6 ${selectedRegion === "서해안" ? "text-white" : "text-amber-600"}`} />
                </div>
                <div>
                  <h3 className="text-lg font-bold">{TRAIL_NAMES["T_ROUTE_MNG0000000043"]}</h3>
                  <div className="text-xs opacity-75">108개 코스 · 1,804km</div>
                </div>
              </div>
              <p className="text-sm opacity-90 leading-relaxed">서쪽 바다의 갯벌과 함께 걷는 길</p>
            </div>

            {/* DMZ 평화의 길 */}
            <div onClick={() => setSelectedRegion("DMZ")} className={`p-5 rounded-xl cursor-pointer transition-all duration-300 group ${selectedRegion === "DMZ" ? "bg-purple-500 text-white shadow-lg scale-105" : "bg-white hover:bg-purple-50 border border-gray-200 hover:shadow-md hover:scale-102"}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${selectedRegion === "DMZ" ? "bg-white/20" : "bg-purple-100 group-hover:bg-purple-200"}`}>
                  <Shield className={`w-6 h-6 ${selectedRegion === "DMZ" ? "text-white" : "text-purple-600"}`} />
                </div>
                <div>
                  <h3 className="text-lg font-bold">{TRAIL_NAMES["T_ROUTE_MNG0000000047"]}</h3>
                  <div className="text-xs opacity-75">48개 코스 · 553km</div>
                </div>
              </div>
              <p className="text-sm opacity-90 leading-relaxed">분단의 아픔을 딛고 평화로 나아가는 길</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white py-8 border-t">
        <div className="container mx-auto px-4">
          {/* Search Bar - Aligned with course grid */}
          <div className="grid lg:grid-cols-[280px_1fr] gap-8 mb-6">
            {/* Empty space for sidebar alignment on desktop */}
            <div className="hidden lg:block"></div>
            
            {/* Search input aligned with course grid */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input 
                data-testid="course-search-input" 
                type="text" 
                placeholder="지역, 코스명으로 검색하세요" 
                value={searchKeyword} 
                onChange={e => setSearchKeyword(e.target.value)} 
                className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-[#F39800] focus:border-transparent" 
              />
            </div>
          </div>

          {/* Main Content Area with Responsive Layout */}
          <div className="relative">
            {/* Desktop Layout (>= 1024px) */}
            <div className="hidden lg:grid lg:grid-cols-[280px_1fr] gap-8">
              {/* Sticky Sidebar Filters */}
              <aside className="sticky top-20 h-fit">
                <div 
                  className="bg-white rounded-xl border border-gray-200 p-6 max-h-[calc(100vh-10rem)] overflow-y-auto"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#CBD5E0 #F7FAFC'
                  }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">필터</h3>
                    {activeFilterCount > 0 && <Badge variant="secondary" className="text-[#F39800] bg-[#F39800]/10">
                        {activeFilterCount}개 적용
                      </Badge>}
                  </div>
                  <Separator className="mb-6" />
                  <FilterContent />
                  
                  {activeFilterCount > 0 && <>
                      <Separator className="my-6" />
                      <Button variant="outline" className="w-full" onClick={() => {
                    setSelectedRegion("전체");
                    setSelectedDistance("전체");
                    setSelectedDuration("전체");
                    setSelectedDifficulty("전체");
                    setSearchKeyword("");
                    setCurrentPage(1);
                  }}>
                        <X className="w-4 h-4 mr-2" />
                        필터 초기화
                      </Button>
                    </>}
                </div>
              </aside>

              {/* Main Content */}
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div className="text-sm text-gray-600">
                    총 <span className="font-semibold text-[#F39800]">{sortedCourses.length}</span>개의 코스가 있습니다
                  </div>
                  <SortDropdown />
                </div>

            {/* Tablet Layout (768px - 1024px) */}
            <div className="hidden md:block lg:hidden">
              {/* Collapsible Horizontal Filters */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">필터</h3>
                  <Button variant="ghost" size="sm" onClick={() => setTabletFiltersCollapsed(!tabletFiltersCollapsed)}>
                    {tabletFiltersCollapsed ? "필터 보기" : "필터 숨기기"}
                    <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${tabletFiltersCollapsed ? "" : "rotate-180"}`} />
                  </Button>
                </div>
                
                {!tabletFiltersCollapsed && <>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <FilterSelect label="지역" value={selectedRegion} onChange={setSelectedRegion} options={regions} />
                      <FilterSelect label="거리" value={selectedDistance} onChange={setSelectedDistance} options={distances} />
                      <FilterSelect label="소요시간" value={selectedDuration} onChange={setSelectedDuration} options={durations} />
                      <FilterSelect label="난이도" value={selectedDifficulty} onChange={setSelectedDifficulty} options={difficulties} />
                    </div>
                    <div className="flex gap-2">
                      <Button className="flex-1" onClick={() => setTabletFiltersCollapsed(true)}>
                        필터 적용
                      </Button>
                      {activeFilterCount > 0 && <Button variant="outline" onClick={() => {
                        setSelectedRegion("전체");
                        setSelectedDistance("전체");
                        setSelectedDuration("전체");
                        setSelectedDifficulty("전체");
                        setSearchKeyword("");
                        setCurrentPage(1);
                      }}>
                          <X className="w-4 h-4" />
                        </Button>}
                    </div>
                  </>}
              </div>
              
              <div className="flex justify-between items-center mb-6">
                <div className="text-sm text-gray-600">
                  총 <span className="font-semibold text-[#F39800]">{sortedCourses.length}</span>개의 코스가 있습니다
                </div>
                <div className="flex items-center gap-3">
                  <SortDropdown />
                  {activeFilterCount > 0 && <Badge variant="secondary" className="text-[#F39800] bg-[#F39800]/10">
                      {activeFilterCount}개 필터 적용
                    </Badge>}
                </div>
              </div>
            </div>

            {/* Mobile Layout (< 768px) */}
            <div className="block md:hidden">
              <div className="flex justify-between items-center mb-6">
                <div className="text-sm text-gray-600">
                  총 <span className="font-semibold text-[#F39800]">{sortedCourses.length}</span>개
                </div>
                <SortDropdown />
              </div>
            </div>

                {/* Course Grid */}
                <div data-testid="course-results">
                  {isLoading ? <>
                      <div className="flex justify-center items-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-[#F39800]" />
                        <span className="ml-2 text-gray-600">코스 정보를 불러오는 중...</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({
                      length: 9
                    }, (_, i) => <CourseCardSkeleton key={i} />)}
                      </div>
                    </> : error ? <div className="text-center py-12">
                      <p className="text-red-600 mb-2">코스 정보를 불러오는 데 실패했습니다.</p>
                      <p className="text-gray-600">{error.message}</p>
                    </div> : sortedCourses.length > 0 ? <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {paginatedCourses.map(course => <CourseCard key={course.course_id} course={course} />)}
                      </div>
                      
                      {/* Pagination */}
                      {totalPages > 1 && <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4">
                          <div className="text-sm text-gray-600">
                            {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, sortedCourses.length)} / {sortedCourses.length}개
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={handlePrevPage} disabled={currentPage === 1} className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg transition-colors ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                              <ChevronLeft className="w-4 h-4" />
                              이전
                            </button>
                            
                            <div className="flex items-center gap-1">
                              {Array.from({
                          length: Math.min(totalPages, 5)
                        }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return <button type="button" key={pageNum} onClick={() => handlePageChange(pageNum)} className={`w-8 h-8 text-sm rounded-lg transition-colors ${currentPage === pageNum ? 'bg-[#F39800] text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                    {pageNum}
                                  </button>;
                        })}
                              
                              {totalPages > 5 && currentPage < totalPages - 2 && <>
                                  <span className="text-gray-400 px-2">...</span>
                                  <button type="button" onClick={() => handlePageChange(totalPages)} className="w-8 h-8 text-sm rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors">
                                    {totalPages}
                                  </button>
                                </>}
                            </div>
                            
                            <button type="button" onClick={handleNextPage} disabled={currentPage === totalPages} className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg transition-colors ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                              다음
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                          
                          <div className="text-sm text-gray-600">
                            {currentPage} / {totalPages} 페이지
                          </div>
                        </div>}
                    </> : <div className="text-center py-16">
                      <p className="text-xl text-gray-500 mb-4">검색 조건에 맞는 코스가 없습니다</p>
                      <p className="text-gray-400 mb-4">다른 조건으로 다시 검색해보세요</p>
                      <button type="button" onClick={() => {
                    setSelectedRegion("전체");
                    setSelectedDistance("전체");
                    setSelectedDuration("전체");
                    setSelectedDifficulty("전체");
                    setSearchKeyword("");
                    setCurrentPage(1);
                  }} className="px-4 py-2 bg-[#F39800] text-white rounded-lg hover:bg-[#E08700] transition-colors">
                        초기화
                      </button>
                    </div>}
                </div>
              </div>
            </div>

            {/* Course Grid for Tablet and Mobile */}
            <div className="block lg:hidden" data-testid="course-results">
              {isLoading ? <>
                  <div className="flex justify-center items-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#F39800]" />
                    <span className="ml-2 text-gray-600">코스 정보를 불러오는 중...</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Array.from({
                  length: 6
                }, (_, i) => <CourseCardSkeleton key={i} />)}
                  </div>
                </> : error ? <div className="text-center py-12">
                  <p className="text-red-600 mb-2">코스 정보를 불러오는 데 실패했습니다.</p>
                  <p className="text-gray-600">{error.message}</p>
                </div> : sortedCourses.length > 0 ? <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {paginatedCourses.map(course => <CourseCard key={course.course_id} course={course} />)}
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="text-sm text-gray-600">
                        {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, sortedCourses.length)} / {sortedCourses.length}개
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={handlePrevPage} disabled={currentPage === 1} className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg transition-colors ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                          <ChevronLeft className="w-4 h-4" />
                          <span className="hidden sm:inline">이전</span>
                        </button>
                        
                        <div className="flex items-center gap-1">
                          {Array.from({
                      length: Math.min(totalPages, 5)
                    }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return <button type="button" key={pageNum} onClick={() => handlePageChange(pageNum)} className={`w-8 h-8 text-sm rounded-lg transition-colors ${currentPage === pageNum ? 'bg-[#F39800] text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                {pageNum}
                              </button>;
                    })}
                          
                          {totalPages > 5 && currentPage < totalPages - 2 && <>
                              <span className="text-gray-400 px-2">...</span>
                              <button type="button" onClick={() => handlePageChange(totalPages)} className="w-8 h-8 text-sm rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors">
                                {totalPages}
                              </button>
                            </>}
                        </div>
                        
                        <button type="button" onClick={handleNextPage} disabled={currentPage === totalPages} className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg transition-colors ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                          <span className="hidden sm:inline">다음</span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="text-sm text-gray-600">
                        {currentPage} / {totalPages}
                      </div>
                    </div>}
                </> : <div className="text-center py-16">
                  <p className="text-xl text-gray-500 mb-4">검색 조건에 맞는 코스가 없습니다</p>
                  <p className="text-gray-400 mb-4">다른 조건으로 다시 검색해보세요</p>
                  <button type="button" onClick={() => {
                setSelectedRegion("전체");
                setSelectedDistance("전체");
                setSelectedDuration("전체");
                setSelectedDifficulty("전체");
                setSearchKeyword("");
                setCurrentPage(1);
              }} className="px-4 py-2 bg-[#F39800] text-white rounded-lg hover:bg-[#E08700] transition-colors">
                    필터 초기화
                  </button>
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Filter Button and Sheet */}
      <div className="md:hidden">
        {/* Fixed Filter Button */}
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-40">
          <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
            <SheetTrigger asChild>
              <Button size="lg" className="shadow-lg rounded-full px-6">
                <Filter className="w-5 h-5 mr-2" />
                필터
                {activeFilterCount > 0 && <Badge variant="secondary" className="ml-2 bg-white text-[#F39800]">
                    {activeFilterCount}
                  </Badge>}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[80vh]">
              <SheetHeader>
                <SheetTitle>필터</SheetTitle>
                <SheetDescription>
                  원하는 조건을 선택하여 코스를 찾아보세요
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 overflow-y-auto h-[calc(100%-8rem)]">
                <FilterContent showApplyButton={true} onApply={() => setMobileFilterOpen(false)} />
              </div>
              {activeFilterCount > 0 && <SheetFooter className="absolute bottom-0 left-0 right-0 p-6 bg-white border-t">
                  <Button variant="outline" className="w-full" onClick={() => {
                setSelectedRegion("전체");
                setSelectedDistance("전체");
                setSelectedDuration("전체");
                setSelectedDifficulty("전체");
                setSearchKeyword("");
                setCurrentPage(1);
                setMobileFilterOpen(false);
              }}>
                    <X className="w-4 h-4 mr-2" />
                    필터 초기화
                  </Button>
                </SheetFooter>}
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <Footer />
    </div>;
};
export default CourseSearch;